// ===========================================
// Template Editor Dialog Component
// ===========================================
// Dialog for editing template customization (subject, preheader)

import { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Divider,
  IconButton,
  Chip,
  Tooltip,
  Paper,
  Stack,
} from '@mui/material';
import DOMPurify from 'dompurify';
import RichTextEditor, { RichTextEditorHandle } from '../../common/RichTextEditor';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import PreviewIcon from '@mui/icons-material/Preview';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import SendTemplateTestButton, { testErrorMessage } from './SendTemplateTestButton';
import {
  getTemplateCustomization,
  setCustomSubject,
  resetCustomSubject,
  setCustomPreheader,
  resetCustomPreheader,
  setCustomBody,
  resetCustomBody,
  generateTemplatePreview,
  MergeField,
} from '../../../api/emailBranding.api';

interface TemplateEditorDialogProps {
  open: boolean;
  templateKey: string;
  /** The row's own name (a reminder step may be renamed); falls back to the template's. */
  templateName?: string;
  onClose: () => void;
  onSave: () => void;
}

type SavePart = 'Subject' | 'Preheader' | 'Email body';

const SAVE_FAILED = 'Failed to save changes. Please try again.';

/** "Email body wasn't saved: Invalid merge fields: … The subject and preheader were saved." */
function saveFailureMessage(failure: { part: SavePart; error: unknown; saved: SavePart[] }): string {
  const raw = testErrorMessage(failure.error, SAVE_FAILED).trim();
  const reason = /[.!?]$/.test(raw) ? raw : `${raw}.`;
  const saved = failure.saved.map((p) => p.toLowerCase());
  const savedNote = saved.length
    ? ` The ${saved.join(' and ')} ${saved.length > 1 ? 'were' : 'was'} saved.`
    : '';
  return `${failure.part} wasn't saved: ${reason}${savedNote}`;
}

/**
 * Generate a starter template based on template metadata
 */
function generateStarterBody(metadata: { name: string; mergeFields: MergeField[] }): string {
  // Find common fields
  const hasParentName = metadata.mergeFields.some(f => f.key === 'parentName');
  const hasTeacherName = metadata.mergeFields.some(f => f.key === 'teacherName');
  const hasStudentName = metadata.mergeFields.some(f => f.key === 'studentName');
  const hasSchoolName = metadata.mergeFields.some(f => f.key === 'schoolName');

  // Determine greeting
  let greeting = 'Hi';
  if (hasParentName) {
    greeting = '<p>Hi {{parentName}},</p>';
  } else if (hasTeacherName) {
    greeting = '<p>Hi {{teacherName}},</p>';
  } else {
    greeting = '<p>Hi,</p>';
  }

  // Build a simple starter template
  const parts = [
    greeting,
    '',
    '<p>Your email content here...</p>',
    '',
  ];

  // Add student reference if available
  if (hasStudentName) {
    parts.push('<p>Regarding {{studentName}}:</p>');
    parts.push('');
  }

  // Add placeholder for main content
  parts.push('<p>[Add your main message here]</p>');
  parts.push('');

  // Add school signature if available
  if (hasSchoolName) {
    parts.push('<p>Best regards,<br>The {{schoolName}} Team</p>');
  } else {
    parts.push('<p>Best regards,<br>The Team</p>');
  }

  return parts.join('\n');
}

export default function TemplateEditorDialog({
  open,
  templateKey,
  templateName,
  onClose,
  onSave,
}: TemplateEditorDialogProps) {
  const queryClient = useQueryClient();

  // Form state
  const [subject, setSubject] = useState('');
  const [preheader, setPreheader] = useState('');
  const [body, setBody] = useState('');
  const [subjectError, setSubjectError] = useState('');
  const [preheaderError, setPreheaderError] = useState('');
  const [bodyError, setBodyError] = useState('');
  const [previewContent, setPreviewContent] = useState<string | null>(null);
  const [previewType, setPreviewType] = useState<'subject' | 'preheader' | 'body' | null>(null);

  // Refs for cursor position tracking
  const subjectRef = useRef<HTMLInputElement>(null);
  const preheaderRef = useRef<HTMLInputElement>(null);
  const bodyEditorRef = useRef<RichTextEditorHandle>(null);

  // #315: did the ADMIN change the body? Quill re-renders whatever it is given (every space becomes
  // &nbsp;, tables and styled boxes are flattened) and reports that as a change, so comparing
  // strings cannot tell an edit from Quill's own normalisation — and an untouched Save used to
  // store the flattened default as the school's custom body. Quill's `source` can tell them apart:
  // 'user' is typing, the toolbar and the merge chips; anything else is the editor loading a value.
  const bodyTouchedRef = useRef(false);
  /** The body as Quill rendered it on load (before any edit), so "typed, then undid" is no change. */
  const bodyBaselineRef = useRef('');

  // Fetch template customization
  const {
    data: customization,
    isLoading,
    isFetching,
    error,
  } = useQuery({
    queryKey: ['templateCustomization', templateKey],
    queryFn: () => getTemplateCustomization(templateKey),
    enabled: open && !!templateKey,
  });

  // Initialize form values when data loads
  useEffect(() => {
    if (customization) {
      setSubject(customization.subject || customization.metadata.defaultSubject);
      setPreheader(customization.preheader || customization.metadata.defaultPreheader);
      // Use existing custom body, or default body from metadata, or generate a starter template
      const existingBody = customization.body?.html;
      let initialBody = '';
      if (existingBody) {
        initialBody = existingBody;
      } else if (customization.metadata.defaultBody) {
        // Use the actual default template body
        initialBody = customization.metadata.defaultBody;
      } else if (customization.metadata.supportsCustomBody) {
        // Fallback to generated starter template
        initialBody = generateStarterBody(customization.metadata);
      }
      setBody(initialBody);
      bodyTouchedRef.current = false;
      // Replaced by Quill's own rendering of it as soon as the editor reports one (source 'api').
      bodyBaselineRef.current = initialBody;
      setSubjectError('');
      setPreheaderError('');
      setBodyError('');
      setPreviewContent(null);
      setPreviewType(null);
    }
  }, [customization]);

  // The last Save's failure: which part, the server's reason, and what saved before it. Held here
  // rather than read off the mutations, whose error outlives the attempt (a step Save skips keeps
  // its old error, and the dialog stays mounted between templates), so a stale reason was shown.
  const [saveFailure, setSaveFailure] = useState<{ part: SavePart; error: unknown; saved: SavePart[] } | null>(null);
  useEffect(() => {
    setSaveFailure(null);
  }, [open, templateKey]);

  // Mutations
  const saveSubjectMutation = useMutation({
    mutationFn: (newSubject: string) => setCustomSubject(templateKey, newSubject),
  });

  const resetSubjectMutation = useMutation({
    mutationFn: () => resetCustomSubject(templateKey),
  });

  const savePreheaderMutation = useMutation({
    mutationFn: (newPreheader: string) => setCustomPreheader(templateKey, newPreheader),
  });

  const resetPreheaderMutation = useMutation({
    mutationFn: () => resetCustomPreheader(templateKey),
  });

  const saveBodyMutation = useMutation({
    mutationFn: (newBody: string) => setCustomBody(templateKey, newBody),
  });

  const resetBodyMutation = useMutation({
    mutationFn: () => resetCustomBody(templateKey),
  });

  const previewMutation = useMutation({
    mutationFn: (contentType: 'subject' | 'preheader' | 'body') =>
      generateTemplatePreview(templateKey, contentType),
    onSuccess: (data) => {
      setPreviewContent(data.rendered);
      setPreviewType(data.contentType);
    },
  });

  // Optimal character limits for mobile visibility
  const SUBJECT_OPTIMAL_LENGTH = 50;
  const PREHEADER_OPTIMAL_LENGTH = 100;

  // Validation
  const validateSubject = (value: string): boolean => {
    if (!value.trim()) {
      setSubjectError('Subject is required');
      return false;
    }
    if (value.length > 200) {
      setSubjectError('Subject cannot exceed 200 characters');
      return false;
    }
    setSubjectError('');
    return true;
  };

  const validatePreheader = (value: string): boolean => {
    if (value.length > 150) {
      setPreheaderError('Preheader cannot exceed 150 characters');
      return false;
    }
    setPreheaderError('');
    return true;
  };

  const validateBody = (value: string): boolean => {
    const maxSize = 100 * 1024; // 100KB
    if (value.length > maxSize) {
      setBodyError('Email body cannot exceed 100KB');
      return false;
    }
    setBodyError('');
    return true;
  };

  // Helper text with optimal length warnings
  const getSubjectHelperText = (): string => {
    if (subjectError) return subjectError;
    const length = subject.length;
    if (length > SUBJECT_OPTIMAL_LENGTH) {
      return `${length}/200 characters (${length - SUBJECT_OPTIMAL_LENGTH} over optimal for mobile)`;
    }
    return `${length}/200 characters`;
  };

  const getPreheaderHelperText = (): string => {
    if (preheaderError) return preheaderError;
    const length = preheader.length;
    if (length > PREHEADER_OPTIMAL_LENGTH) {
      return `${length}/150 characters (${length - PREHEADER_OPTIMAL_LENGTH} over optimal for preview)`;
    }
    return `${length}/150 characters`;
  };

  // Check if over optimal length
  const isSubjectOverOptimal = subject.length > SUBJECT_OPTIMAL_LENGTH;
  const isPreheaderOverOptimal = preheader.length > PREHEADER_OPTIMAL_LENGTH;

  const getBodyHelperText = (): string => {
    if (bodyError) return bodyError;
    const sizeKB = (body.length / 1024).toFixed(1);
    return `${sizeKB}KB / 100KB`;
  };

  // Handlers
  const handleSave = async () => {
    const subjectValid = validateSubject(subject);
    const preheaderValid = validatePreheader(preheader);
    const bodyValid = metadata?.supportsCustomBody ? validateBody(body) : true;

    if (!subjectValid || !preheaderValid || !bodyValid) return;

    setSaveFailure(null);
    let part: SavePart = 'Subject';
    const saved: SavePart[] = [];

    try {
      // Check if subject has changed from default
      const defaultSubject = customization?.metadata.defaultSubject || '';
      const defaultPreheader = customization?.metadata.defaultPreheader || '';

      // Save subject if different from default
      if (subject !== defaultSubject) {
        await saveSubjectMutation.mutateAsync(subject);
        saved.push(part);
      } else if (customization?.isSubjectCustomized) {
        // Reset if back to default
        await resetSubjectMutation.mutateAsync();
        saved.push(part);
      }

      // Save preheader if different from default
      part = 'Preheader';
      if (preheader !== defaultPreheader) {
        await savePreheaderMutation.mutateAsync(preheader);
        saved.push(part);
      } else if (customization?.isPreheaderCustomized) {
        // Reset if back to default
        await resetPreheaderMutation.mutateAsync();
        saved.push(part);
      }

      // Save body if template supports it — and only if the admin actually changed it (#315).
      part = 'Email body';
      if (metadata?.supportsCustomBody) {
        const intent = bodyIntent();
        if (intent === 'save') {
          await saveBodyMutation.mutateAsync(body);
        } else if (intent === 'reset' && customization?.isBodyCustomized) {
          // Cleared, or reverted to the default/starter wording
          await resetBodyMutation.mutateAsync();
        }
      }

      invalidateSavedTemplate(true);
      onSave();
    } catch (error) {
      setSaveFailure({ part, error, saved });
      // Part of the save may have landed (subject saved, body 400'd), so mark the cache stale —
      // but WITHOUT refetching: a refetch would run the form-reset effect above and wipe the
      // admin's unsaved edits while they read the error.
      invalidateSavedTemplate(false);
    }
  };

  /**
   * The app caches queries for 5 minutes, so without this the editor reopened and the "Preview
   * email" dialog both kept showing the OLD wording after a save, which reads exactly like the
   * save did nothing.
   */
  function invalidateSavedTemplate(refetch: boolean) {
    const refetchType = refetch ? 'active' : 'none';
    queryClient.invalidateQueries({ queryKey: ['templateCustomization', templateKey], refetchType });
    queryClient.invalidateQueries({ queryKey: ['fullEmailPreview', templateKey], refetchType });
  }

  /** Cleared, or still the default/starter body: Save resets it rather than storing it. */
  function isUncustomisedBody(value: string): boolean {
    if (!metadata) return true;
    return !value.trim() || value === (metadata.defaultBody || '') || value === generateStarterBody(metadata);
  }

  /**
   * What Save would do with the body (#315), so Save and "Send test email" can never disagree:
   *  - `unchanged`: the admin never edited it, or typed and undid back to what was loaded. An
   *    untouched body is NEVER stored — Quill's reformatting of the default is not an edit.
   *  - `reset`: cleared, or back to the default/starter wording → the default is used.
   *  - `save`: genuinely new wording.
   */
  function bodyIntent(): 'unchanged' | 'reset' | 'save' {
    if (!bodyTouchedRef.current) return 'unchanged';
    if (isUncustomisedBody(body)) return 'reset';
    if (body === bodyBaselineRef.current || body === (customization?.body?.html || '')) return 'unchanged';
    return 'save';
  }

  /** What "Send test email" renders: the fields as they are now, before saving. */
  const getTestDraft = () => {
    const intent = metadata?.supportsCustomBody ? bodyIntent() : 'unchanged';
    return {
      subject,
      preheader,
      // undefined = what is saved (else the default); '' = the default; else the edited wording.
      bodyHtml: intent === 'save' ? body : intent === 'reset' ? '' : undefined,
    };
  };

  const handleResetSubject = () => {
    if (customization?.metadata.defaultSubject) {
      setSubject(customization.metadata.defaultSubject);
      setSubjectError('');
    }
  };

  const handleResetPreheader = () => {
    if (customization?.metadata.defaultPreheader) {
      setPreheader(customization.metadata.defaultPreheader);
      setPreheaderError('');
    }
  };

  const handleResetBody = () => {
    // Clearing is an explicit choice: Save then resets the body to the default.
    bodyTouchedRef.current = true;
    setBody('');
    setBodyError('');
  };

  const handlePreview = (type: 'subject' | 'preheader' | 'body') => {
    previewMutation.mutate(type);
  };

  const insertMergeField = (e: React.MouseEvent, field: MergeField, target: 'subject' | 'preheader' | 'body') => {
    e.stopPropagation();
    e.preventDefault();

    if (target === 'body') {
      // A layout block goes on a line of its own (#315): the server lifts it out of its paragraph,
      // but only when the paragraph holds nothing else.
      bodyEditorRef.current?.insertText(field.block ? `\n${field.placeholder}\n` : field.placeholder);
      return;
    }

    const refMap = { subject: subjectRef, preheader: preheaderRef };
    const setterMap = { subject: setSubject, preheader: setPreheader };
    const inputEl = refMap[target].current;
    const setter = setterMap[target];

    if (inputEl) {
      const start = inputEl.selectionStart ?? inputEl.value.length;
      const end = inputEl.selectionEnd ?? start;
      const text = field.placeholder;
      setter(inputEl.value.slice(0, start) + text + inputEl.value.slice(end));
      // Restore cursor position after the inserted text
      requestAnimationFrame(() => {
        inputEl.focus();
        inputEl.setSelectionRange(start + text.length, start + text.length);
      });
    } else {
      setter((prev) => prev + field.placeholder);
    }
  };

  const isSaving =
    saveSubjectMutation.isPending ||
    resetSubjectMutation.isPending ||
    savePreheaderMutation.isPending ||
    resetPreheaderMutation.isPending ||
    saveBodyMutation.isPending ||
    resetBodyMutation.isPending;


  // Show loading state when fetching or when query hasn't started yet
  if (isLoading || isFetching || (!customization && !error)) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
            <CircularProgress />
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  // Only show error state if there was an actual error
  if (error) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogContent>
          <Alert severity="error">Failed to load template customization</Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  }

  // Safety check - should not reach here if customization is undefined
  if (!customization) {
    return null;
  }

  const { metadata } = customization;

  // #315: a body saved before the amount box / Pay button became fields (or edited without them)
  // is sent WITHOUT them. Judged on the SAVED body, not the live editor value.
  const missingBlocks = customization.isBodyCustomized
    ? metadata.mergeFields.filter(
        (f) => f.block && f.required && !(customization.body?.html || '').includes(f.placeholder)
      )
    : [];

  // #372: with late fees on, the Last Chance wording must tell the family the fee and the date.
  // Satisfied by a blank body (the default carries the block) or a body holding {{lateFeeNotice}}.
  // Judged on the SAVED body; a warning, never a block.
  const lateFeeFields = metadata.mergeFields.filter((f) => f.warnWhenMissing === 'lateFees');
  const savedBodyHtml = customization.body?.html || '';
  const bodyIsBlank = savedBodyHtml.replace(/<[^>]*>|&nbsp;/g, '').trim() === '';
  const hasNotice = /\{\{\s*lateFeeNotice\s*\}\}/.test(savedBodyHtml);
  const lateFeeSentenceMissing =
    !!customization.conditionalFields?.lateFees &&
    customization.isBodyCustomized &&
    lateFeeFields.length > 0 &&
    !(bodyIsBlank || hasNotice);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6">{templateName || metadata.name}</Typography>
            <Typography variant="body2" color="text.secondary">
              {metadata.description}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* The server's reason, e.g. "Invalid merge fields: …": the generic message alone left MNM
            unable to tell what to fix (2026-10-03). */}
        {saveFailure && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {saveFailureMessage(saveFailure)}
          </Alert>
        )}

        {missingBlocks.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Your saved wording doesn't include {missingBlocks.map((f) => f.placeholder).join(' or ')}, so this
            email is sent without {missingBlocks.length > 1 ? 'them' : 'it'}. Insert{' '}
            {missingBlocks.length > 1 ? 'them' : 'it'} on a line of {missingBlocks.length > 1 ? 'their' : 'its'} own
            where {missingBlocks.length > 1 ? 'they belong' : 'it belongs'}, or clear the body to go back to the
            standard layout.
          </Alert>
        )}

        {lateFeeSentenceMissing && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Late fees are on, but this email doesn't include the Late fee notice, so families won't be told the fee or its date.
          </Alert>
        )}

        {/* Merge Fields Reference */}
        <Box mb={3}>
          <Typography variant="subtitle2" gutterBottom>
            Available Merge Fields
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" mb={1}>
            Dynamic placeholders that get replaced with actual values when email is sent
          </Typography>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {metadata.mergeFields.map((field) => (
              <Tooltip
                key={field.key}
                title={
                  <Box>
                    <Typography variant="body2">{field.description}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Example: {field.example}
                    </Typography>
                  </Box>
                }
                arrow
              >
                <Chip
                  label={field.placeholder}
                  size="small"
                  color={field.required ? 'primary' : 'default'}
                  variant="outlined"
                  sx={{ mb: 0.5 }}
                />
              </Tooltip>
            ))}
          </Stack>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Subject Line */}
        <Box mb={3}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="subtitle2">
              Subject Line
              {customization.isSubjectCustomized && (
                <Chip
                  label="Customized"
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                />
              )}
            </Typography>
            <Box>
              <Tooltip title="Preview with sample data">
                <IconButton
                  size="small"
                  onClick={() => handlePreview('subject')}
                  disabled={previewMutation.isPending}
                >
                  <PreviewIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Reset to default">
                <IconButton
                  size="small"
                  onClick={handleResetSubject}
                  disabled={subject === metadata.defaultSubject}
                >
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          <TextField
            fullWidth
            size="small"
            value={subject}
            inputRef={subjectRef}
            onChange={(e) => {
              setSubject(e.target.value);
              validateSubject(e.target.value);
            }}
            error={!!subjectError}
            helperText={getSubjectHelperText()}
            placeholder="Enter custom subject line..."
            FormHelperTextProps={{
              sx: {
                color: !subjectError && isSubjectOverOptimal ? 'warning.main' : undefined,
              },
            }}
          />
          <Box mt={1}>
            <Typography variant="caption" color="text.secondary">
              Insert:
            </Typography>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap component="span" sx={{ ml: 0.5 }}>
              {metadata.mergeFields.filter((f) => !f.block).slice(0, 4).map((field) => (
                <Chip
                  key={`subject-${field.key}`}
                  label={field.placeholder}
                  size="small"
                  onClick={(e) => insertMergeField(e, field, 'subject')}
                  variant="outlined"
                  sx={{ cursor: 'pointer', height: 20, fontSize: '0.7rem' }}
                />
              ))}
            </Stack>
          </Box>
          <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
            Default: {metadata.defaultSubject}
          </Typography>
        </Box>

        {/* Preheader */}
        <Box mb={3}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="subtitle2">
              Preheader (Preview Text)
              {customization.isPreheaderCustomized && (
                <Chip
                  label="Customized"
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                />
              )}
            </Typography>
            <Box>
              <Tooltip title="Preview with sample data">
                <IconButton
                  size="small"
                  onClick={() => handlePreview('preheader')}
                  disabled={previewMutation.isPending}
                >
                  <PreviewIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Reset to default">
                <IconButton
                  size="small"
                  onClick={handleResetPreheader}
                  disabled={preheader === metadata.defaultPreheader}
                >
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          <TextField
            fullWidth
            size="small"
            multiline
            rows={2}
            value={preheader}
            inputRef={preheaderRef}
            onChange={(e) => {
              setPreheader(e.target.value);
              validatePreheader(e.target.value);
            }}
            error={!!preheaderError}
            helperText={getPreheaderHelperText()}
            placeholder="Enter custom preheader text..."
            FormHelperTextProps={{
              sx: {
                color: !preheaderError && isPreheaderOverOptimal ? 'warning.main' : undefined,
              },
            }}
          />
          <Box mt={1}>
            <Typography variant="caption" color="text.secondary">
              Insert:
            </Typography>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap component="span" sx={{ ml: 0.5 }}>
              {metadata.mergeFields.filter((f) => !f.block).slice(0, 4).map((field) => (
                <Chip
                  key={`preheader-${field.key}`}
                  label={field.placeholder}
                  size="small"
                  onClick={(e) => insertMergeField(e, field, 'preheader')}
                  variant="outlined"
                  sx={{ cursor: 'pointer', height: 20, fontSize: '0.7rem' }}
                />
              ))}
            </Stack>
          </Box>
          <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
            Default: {metadata.defaultPreheader}
          </Typography>
        </Box>

        {/* Preview Panel */}
        {previewContent && (
          <Box mb={2}>
            <Typography variant="subtitle2" gutterBottom>
              Preview ({previewType === 'subject' ? 'Subject' : previewType === 'preheader' ? 'Preheader' : 'Body'})
            </Typography>
            <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default', maxHeight: 300, overflow: 'auto' }}>
              {previewType === 'body' ? (
                <Box dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(previewContent) }} sx={{ '& a': { color: 'primary.main' } }} />
              ) : (
                <Typography variant="body2">{previewContent}</Typography>
              )}
            </Paper>
          </Box>
        )}

        {/* Body Customization */}
        {metadata.supportsCustomBody && (
          <>
            <Divider sx={{ my: 3 }} />
            <Box mb={3}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="subtitle2">
                  Email Body (HTML)
                  {customization.isBodyCustomized && (
                    <Chip
                      label="Customized"
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                    />
                  )}
                </Typography>
                <Box>
                  <Tooltip title="Preview with sample data">
                    <IconButton
                      size="small"
                      onClick={() => handlePreview('body')}
                      disabled={previewMutation.isPending || !body.trim()}
                    >
                      <PreviewIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Clear body (use default)">
                    <IconButton
                      size="small"
                      onClick={handleResetBody}
                      disabled={!body.trim()}
                    >
                      <RefreshIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
              <RichTextEditor
                ref={bodyEditorRef}
                value={body}
                onChange={(content, source) => {
                  if (source === 'user') bodyTouchedRef.current = true;
                  else if (!bodyTouchedRef.current) bodyBaselineRef.current = content;
                  setBody(content);
                  validateBody(content);
                }}
                placeholder="Enter your email body content... Use the merge field chips below to insert dynamic placeholders."
                height={300}
                helperText={getBodyHelperText()}
                enableImageUpload={false}
              />
              <Box mt={1}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Insert merge fields:
                </Typography>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                  {metadata.mergeFields.map((field) => (
                    <Chip
                      key={`body-${field.key}`}
                      label={field.placeholder}
                      size="small"
                      onClick={(e) => insertMergeField(e, field, 'body')}
                      color={field.required ? 'primary' : 'default'}
                      variant="outlined"
                      sx={{ cursor: 'pointer', height: 24, fontSize: '0.75rem' }}
                    />
                  ))}
                </Stack>
              </Box>
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  Use the toolbar to format your email body. Insert merge field placeholders using the chips below — they will be replaced with real values when the email is sent.
                </Typography>
                {metadata.mergeFields.some((f) => f.block) && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Some fields, like {metadata.mergeFields.filter((f) => f.block).map((f) => f.placeholder).join(', ')},
                    are ready-made sections that are filled in when the email is sent: keep each on a line of its
                    own. Use Send test email or Preview to see them.
                  </Typography>
                )}
              </Alert>
            </Box>
          </>
        )}
      </DialogContent>

      {/* Wraps on a phone: three buttons do not fit a 360px row. */}
      <DialogActions sx={{ flexWrap: 'wrap', rowGap: 1 }}>
        <Box sx={{ mr: 'auto' }}>
          <SendTemplateTestButton
            templateKey={templateKey}
            getDraft={getTestDraft}
            disabled={isSaving || !!subjectError || !!preheaderError || !!bodyError}
          />
        </Box>
        <Button onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={isSaving || !!subjectError || !!preheaderError || !!bodyError}>
          {isSaving ? <CircularProgress size={20} /> : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
